/**
 * Erros do hook.ai.run / hook.ai.budget.
 *
 * Hierarquia: AiError estende SdkError pra integrar com try/catch existente.
 * Subtipos permitem identificar caso especifico via instanceof.
 */
import { SdkError, SdkAuthError, SdkRateLimitError, SdkValidationError } from '../client/errors';

export class AiError extends SdkError {
  constructor(message: string, code: string, httpStatus: number) {
    super(message, code, httpStatus);
    this.name = 'AiError';
  }
}

/** 404 — task_key nao existe ou esta disabled (app_ai_tasks.enabled=false). */
export class AiTaskNotFoundError extends AiError {
  constructor(message: string, code: string) {
    super(message, code, 404);
    this.name = 'AiTaskNotFoundError';
  }
}

/** 400 — input do app nao bate com input_schema da task. */
export class AiInputError extends SdkValidationError {
  constructor(message: string, code: string) {
    super(message, code, 400);
    this.name = 'AiInputError';
  }
}

/** 402 — app estourou monthly_budget_cents. UI deve mostrar barra cheia. */
export class AiBudgetExceededError extends AiError {
  constructor(message: string, code: string) {
    super(message, code, 402);
    this.name = 'AiBudgetExceededError';
  }
}

/**
 * 429 — rate limit. scope indica qual nivel estourou (user|app|system).
 * App pode usar pra UX diferente em cada caso.
 */
export class AiRateLimitError extends SdkRateLimitError {
  readonly scope: 'user' | 'app' | 'system' | 'unknown';

  constructor(message: string, code: string, retryAfter: number) {
    super(message, code, retryAfter);
    this.name = 'AiRateLimitError';
    const m = /:(user|app|system)$/.exec(code);
    this.scope = (m?.[1] as 'user' | 'app' | 'system') ?? 'unknown';
  }
}

/** 502 — provider externo retornou erro ou shape invalido. */
export class AiProviderError extends AiError {
  constructor(message: string, code: string) {
    super(message, code, 502);
    this.name = 'AiProviderError';
  }
}

/** 504 — provider timeout. Retry geralmente nao ajuda imediato. */
export class AiTimeoutError extends AiError {
  constructor(message: string, code: string) {
    super(message, code, 504);
    this.name = 'AiTimeoutError';
  }
}

/** 401 — nao autenticado. App deve redirect pra login. */
export class AiUnauthorizedError extends SdkAuthError {
  constructor(message: string, code: string) {
    super(message, code);
    this.name = 'AiUnauthorizedError';
  }
}

/**
 * Mapeia HTTP status + body do backend para erro tipado.
 * Body shape: { error: string, code: string, retryAfter?: number }.
 */
export async function mapAiError(res: Response): Promise<Error> {
  let body: { error?: string; code?: string; logCode?: string; retryAfter?: number } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    // body nao-JSON; mantem defaults
  }
  const message = body.error ?? `AI request failed (${res.status})`;
  const code = body.code ?? body.logCode ?? `ai.unknown:${res.status}`;

  switch (res.status) {
    case 401:
      return new AiUnauthorizedError(message, code);
    case 400:
      return new AiInputError(message, code);
    case 402:
      return new AiBudgetExceededError(message, code);
    case 404:
      return new AiTaskNotFoundError(message, code);
    case 429: {
      const retryAfter =
        body.retryAfter ?? Number(res.headers.get('Retry-After')) ?? 60;
      return new AiRateLimitError(message, code, retryAfter);
    }
    case 504:
      return new AiTimeoutError(message, code);
    case 502:
      return new AiProviderError(message, code);
    default:
      return new AiError(message, code, res.status);
  }
}
